<template>
  <b-modal
    v-model="editorShown"
    :title="medicationId ? 'Edit Medication' : 'Administer PRN medication'"
    @hidden="onHidden"
    @ok="submitMedication"
    @cancel="close"
    @close="close"
    centered
    size="md"
    no-close-on-backdrop
    header-bg-variant="primary"
    header-text-variant="white"
    header-class="bg-purple"
  >
    <template v-slot:modal-footer="{ ok }">
      <b-button variant="secondary" @click="close()">
        Cancel
      </b-button>
      <b-button variant="primary" @click="ok()">
        {{ medicationId ? "Update" : "Create" }}
      </b-button>
    </template>
    <b-row>
      <b-col>
        <div
          v-if="medicationId && formPrn.patient"
          class="border rounded p-2 mb-2 color-custom-dark-grey"
          style="font-size: 14px;"
        >
          <div>
            Patient:
            {{ formatEocObj(formPrn.patient) }}
          </div>
          <div>
            Medication:
            {{ formPrn.medication.name }}
          </div>
          <div>
            Dosage:
            {{ formPrn.dosageAmount }} {{ formPrn.unitOfMeasurement.name }}
          </div>
        </div>
        <b-form-group
          v-if="!medicationId"
          label="Patient (Active & Pending only)"
        >
          <eoc-picker
            class=""
            :eocs="activeClients"
            v-model="formPrn.eoc"
            :async="true"
            @search="
              ({ search, loading }) => fetchEocsOptionsPicker(search, loading)
            "
        /></b-form-group>
        <b-form-group
          v-if="!medicationId"
          label="Medications"
          :state="formPrnErrors.prnMedication.length == 0 ? null : false"
          :invalid-feedback="formPrnErrors.prnMedication.join(', ')"
        >
          <multiple-select
            :options="formPrnMedications"
            v-model="formPrn.prnMedication"
            :disableCache="true"
          />
        </b-form-group>
        <b-row>
          <b-col>
            <b-form-group
              label="Date"
              :state="formPrnErrors.date.length == 0 ? null : false"
              :invalid-feedback="formPrnErrors.date.join(', ')"
            >
              <date-picker
                v-model="formPrn.date"
                :errors="formPrnErrors.date"
                :min="startDate"
                :max="stopDate"
              ></date-picker>
            </b-form-group>
          </b-col>
          <b-col>
            <b-form-group label="Time" class="time-container">
              <b-time
                hide-header
                :hour12="!selectedLocationObj.marsMilitaryTimes"
                v-model="formPrn.time"
                :state="formPrnErrors.time.length == 0 ? null : false"
                :invalid-feedback="formPrnErrors.time.join(', ')"
              ></b-time>
            </b-form-group>
          </b-col>
        </b-row>
        <b-form-group
          label="Status"
          :state="formPrnErrors.status.length == 0 ? null : false"
          :invalid-feedback="formPrnErrors.status.join(', ')"
        >
          <status-picker :options="marsStatuses" v-model="formPrn.status" />
        </b-form-group>
        <b-form-group v-if="formPrn.status.value == 'Given'" label="Given By">
          <v-select
            class=""
            placeholder=""
            v-model="formPrn.givenBy"
            :options="users"
            :disabled="formPrn.status.value != 'Given'"
            :clearable="false"
          ></v-select>
        </b-form-group>
        <b-form-group
          v-if="formPrn.status.value == 'Given'"
          class="my-4"
          label="Signature"
          :state="formPrnErrors.signature.length == 0 ? null : false"
          :invalid-feedback="formPrnErrors.signature.join(', ')"
        >
          <div>
            <signature v-model="formPrn.signature" ref="signature"> </signature>
          </div>
        </b-form-group>

        <b-form-group label="Notes">
          <b-form-textarea
            placeholder=""
            v-model="formPrn.notes"
          ></b-form-textarea>
        </b-form-group>
      </b-col>
    </b-row>
  </b-modal>
</template>

<script>
import moment from "moment";
import marsServer from "../../server/marsRecord";
import {
  formatDateTimeSql,
  formatDate,
  formatDateOnlyMysql,
  formatTimeOnlyMysql
} from "../../helpers/date";
import { mapActions, mapMutations, mapState, mapGetters } from "vuex";
import DatePicker from "../controls/DatePicker";
import EocPicker from "../controls/EocPicker";
import { validateRequired, validateDate } from "../../helpers/validation";
import StatusPicker from "../controls/StatusPicker.vue";
import eocServer from "../../server/eoc";
import prnMedicationServer from "../../server/prnMedication";
import userServer from "../../server/user";
import MultipleSelect from "../controls/MultipleSelect.vue";
import Signature from "../controls/Signature.vue";

export default {
  name: "MarsAddEditMedicationModal",
  components: {
    DatePicker,
    EocPicker,
    StatusPicker,
    MultipleSelect,
    Signature
  },
  props: ["medicationId"],
  data() {
    return {
      submit: null,
      showSignPad: false,
      formPrn: {
        patient: null,
        medication: null,
        dateTime: "",
        type: "Prn",
        eoc: null,
        prnMedication: [],
        date: null,
        time: null,
        status: "",
        notes: "",
        givenBy: null,
        signature: null
      },
      formPrnErrors: {
        eoc: [],
        prnMedication: [],
        date: [],
        time: [],
        status: [],
        signature: []
      },
      activeClients: [],
      formPrnMedications: [],
      marsStatuses: [
        {
          value: "Pending",
          label: "Pending",
          itemClass: "text-warning",
          variant: "warning"
        },
        {
          value: "NotGiven",
          label: "Not Given",
          itemClass: "text-danger",
          variant: "danger"
        },
        {
          value: "Given",
          label: "Given",
          itemClass: "text-success",
          variant: "success"
        }
      ],
      users: []
    };
  },
  async mounted() {
    if (!this.medicationId) {
      this.getActiveClients();
    }

    this.getUsers();
    const currentUserOption = {
      value: this.currentUser.id,
      label: this.formatUser(this.currentUser)
    };

    if (this.medicationId) {
      const medication = await marsServer.get(this.medicationId);
      this.formPrn = {
        patient: medication.episodeOfCare,
        medication: medication.medication,
        dosageAmount: medication.dosageAmount,
        unitOfMeasurement: medication.unitOfMeasurement,
        dateTime: formatDate(medication.dateTime),
        type: medication.type,
        eoc: null,
        prnMedication: [],
        date: formatDateOnlyMysql(medication.dateTime),
        time: formatTimeOnlyMysql(medication.dateTime).substring(0, 5),
        status: this.marsStatuses.find(mS => mS.value == medication.status),
        notes: medication.notes ? medication.notes : "",
        givenBy: medication.givenBy
          ? {
              value: medication.givenBy.id,
              label: this.formatUser(medication.givenBy)
            }
          : currentUserOption,
        signature: medication.signature
          ? {
              signature: medication.signature,
              user: medication.signedBy
                ? {
                    id: medication.signedBy.id,
                    firstName: medication.signedBy.firstName,
                    lastName: medication.signedBy.lastName,
                    middleName: medication.signedBy.middleName,
                    credentials: medication.signedBy.credentials
                  }
                : ""
            }
          : null
      };
      await this.fetchFormPrnMedications(medication.episodeOfCare.id);
      var prnMedication = this.formPrnMedications.find(
        m => m.data.id == medication.prnMedicationId
      );
      if (prnMedication) {
        this.formPrn.prnMedication.push(prnMedication);
      }
    } else {
      const rounded = moment.duration(15, "minutes");
      const currentAdjustedDateTime = moment(
        Math.ceil(+this.getCurrentAdjustedDateTime() / rounded) * rounded
      );
      this.formPrn.date = formatDateOnlyMysql(currentAdjustedDateTime);
      this.formPrn.time = formatTimeOnlyMysql(
        currentAdjustedDateTime
      ).substring(0, 5);
    }
  },
  methods: {
    ...mapActions("layout", ["showAlertSuccess"]),
    ...mapMutations("client", ["dispatchListener"]),
    onHidden() {},
    async fetchEocsOptionsPicker(search, loading) {
      if (this.lastTimeoutEocsOptionsPicker) {
        clearTimeout(this.lastTimeoutEocsOptionsPicker);
        this.lastTimeoutEocsOptionsPicker = null;
      }
      if (search && search.trim().length >= 3) {
        this.activeClients = [];
      }
      this.lastTimeoutEocsOptionsPicker = setTimeout(async () => {
        if (!search || search.length == 0 || search.length >= 3) {
          if (search.length >= 3) {
            loading(true);
          }

          this.searchingEocs = true;
          const optionsResult = await eocServer.queryList(
            {
              searchPhrase: search,
              eocStatus:
                !search || search.length == 0 ? ["Active", "Pending"] : [],
              locations: this.locationId ? [this.locationId] : [],
              page: 1,
              pageSize: 50
            },
            {
              "Hide-Loading": true
            }
          );

          this.activeClients = optionsResult.episodesOfCare.map(eoc => ({
            value: eoc.id,
            label: this.formatEocObj(eoc),
            data: eoc
          }));
          this.searchingEocs = false;
          if (search.length >= 3) {
            loading(false);
          }
        }
      }, 1000);
    },
    validateMedicationSubmit() {
      let withoutErrors = true;
      var formPrnDateTime = formatDateTimeSql(
        this.formPrn.date + " " + this.formPrn.time
      );

      this.formPrnErrors.eoc = [];
      if (!this.medicationId && !validateRequired(this.formPrn.eoc)) {
        this.formPrnErrors.eoc.push("This field is required");
        withoutErrors = false;
      }

      this.formPrnErrors.prnMedication = [];
      if (!this.medicationId && !validateRequired(this.formPrn.prnMedication)) {
        this.formPrnErrors.prnMedication.push("This field is required");
        withoutErrors = false;
      }

      this.formPrnErrors.status = [];
      if (!validateRequired(this.formPrn.status)) {
        this.formPrnErrors.status.push("This field is required");
        withoutErrors = false;
      }

      this.formPrnErrors.date = [];
      if (!validateRequired(this.formPrn.date)) {
        this.formPrnErrors.date.push("This field is required");
        withoutErrors = false;
      }
      if (this.formPrn.date && !validateDate(this.formPrn.date)) {
        this.formPrnErrors.date.push(
          "This field must have a valid date format"
        );
        withoutErrors = false;
      }
      if (this.formPrn.type == "Prn") {
        if (this.startDate && formPrnDateTime < this.startDate) {
          this.formPrnErrors.date.push(
            "Date & time not valid for selected medications"
          );
          withoutErrors = false;
        } else if (this.stopDate && formPrnDateTime > this.stopDate) {
          this.formPrnErrors.date.push(
            "Date & time not valid for selected medications"
          );
          withoutErrors = false;
        }
      }

      this.formPrnErrors.time = [];
      if (!validateRequired(this.formPrn.time)) {
        this.formPrnErrors.time.push("This field is required");
        withoutErrors = false;
      }

      this.formPrnErrors.signature = [];
      if (this.formPrn.status.value == "Given" && !this.formPrn.signature) {
        this.formPrnErrors.signature.push("A signature is required");
        withoutErrors = false;
      }

      return withoutErrors;
    },
    async submitMedication(modalEvent) {
      modalEvent.preventDefault();
      if (this.$refs.signature) {
        this.formPrn.signature = this.$refs.signature.getSignatureValue();
      }

      const result = await this.validateMedicationSubmit();
      if (result) {
        let result = null;
        if (this.medicationId) {
          result = await marsServer.update(this.medicationId, {
            dateTime: formatDateTimeSql(
              this.formPrn.date + " " + this.formPrn.time
            ),
            status: this.formPrn.status.value,
            notes: this.formPrn.notes,
            givenBy: this.formPrn.givenBy ? this.formPrn.givenBy.value : null,
            signedBy: this.formPrn.signature
              ? this.formPrn.signature.user.id
              : null,
            signature: this.formPrn.signature
              ? this.formPrn.signature.signature
              : null
          });
        } else {
          result = await marsServer.create({
            dateTime: formatDateTimeSql(
              this.formPrn.date + " " + this.formPrn.time
            ),
            prnMedicationIds: this.formPrn.prnMedication.map(u => u.value),
            status: this.formPrn.status.value,
            notes: this.formPrn.notes,
            givenBy: this.formPrn.givenBy ? this.formPrn.givenBy.value : null,
            signedBy: this.formPrn.signature
              ? this.formPrn.signature.user.id
              : null,
            signature: this.formPrn.signature
              ? this.formPrn.signature.signature
              : null
          });
        }
        this.showAlertSuccess();
        this.dispatchFetchEoc();
        this.disconectSigWeb();
        this.$emit("onModify", result);
      } else {
        //this.$dialog.alert("You have some errors in the form");
      }
    },
    dispatchFetchEoc() {
      this.dispatchListener({
        listener: "fetchEoc",
        param: "all"
      });
    },
    async getActiveClients() {
      const optionsResult = await eocServer.queryList({
        searchPhrase: "",
        eocStatus: ["Active", "Pending"],
        locationId: [],
        page: 1,
        pageSize: 50
      });
      this.activeClients = optionsResult.episodesOfCare.map(eoc => ({
        value: eoc.id,
        label: this.formatEocObj(eoc),
        data: eoc
      }));
    },
    async fetchFormPrnMedications(eocId = this.formPrn.eoc.value) {
      if (eocId) {
        const optionsResult = await prnMedicationServer.query(eocId, {});
        this.formPrnMedications = optionsResult.map(prnMedication => ({
          value: prnMedication.id,
          label: `${prnMedication.medication?.name} / ${prnMedication.dosageAmount} ${prnMedication.unitOfMeasurement?.name} / ${prnMedication.medicationRoute?.name}`,
          data: prnMedication
        }));
      } else this.formPrnMedications = [];
    },
    async getUsers() {
      const users = await userServer.getMinimalUsers();
      this.users = users.map(user => ({
        value: user.id,
        label: this.formatUser(user)
      }));
    },
    disconectSigWeb() {
      if (this.$refs.signature) {
        this.$refs.signature.disconectSigWeb();
      }
    },
    close() {
      this.disconectSigWeb();
      this.$emit("onModify");
    }
  },
  computed: {
    ...mapGetters("layout", ["getCurrentAdjustedDateTime"]),
    ...mapState("layout", {
      currentUser: state => state.currentUser,
      selectedLocationObj: state => state.selectedLocationObj
    }),
    editorShown: {
      get() {
        return !!this.$attrs.value;
      },
      set(value) {
        this.$emit("input", value);
      }
    },
    startDate() {
      const length = this.formPrn.prnMedication.length;
      return length
        ? this.formPrn.prnMedication
            .filter(u => u.data.startDate)
            .map(u => u.data.startDate)
            .sort((a, b) => a.localeCompare(b))[length - 1]
        : null;
    },
    stopDate() {
      const length = this.formPrn.prnMedication.length;
      return length
        ? this.formPrn.prnMedication
            .filter(u => u.data.stopDate)
            .map(u => u.data.stopDate)
            .sort((a, b) => a.localeCompare(b))[0]
        : null;
    }
  },
  watch: {
    "formPrn.eoc": function() {
      this.formPrn.prnMedication = [];
      this.fetchFormPrnMedications();
    },
    "formPrn.status": function() {
      if (this.formPrn.status.value != "Given") {
        this.formPrn.givenBy = "";
        this.formPrn.signature = null;
        this.showSignPad = false;
      } else {
        const currentUserOption = {
          value: this.currentUser.id,
          label: this.formatUser(this.currentUser)
        };
        this.formPrn.givenBy = currentUserOption;
      }
    }
  }
};
</script>

<style scoped>
.time-container >>> .b-time {
  min-width: initial !important;
}
</style>
